import React from 'react';
import { LockOpen } from 'lucide-react';

import { StatusBadge } from 'components/pages/certificates/StatusBadge';
import { border } from 'settings/tokens';

/**
 * Neutral zinc ramp — deliberately NOT a status colour.
 *
 * "Opened" is a fact about the seal, not a problem with the item: on a Closure tag the
 * owner opening their own bottle is the expected, common case. It previously reused the
 * `tampered` ramp, which is byte-identical to `stolen` (`#FEE2E2` / `#7F1D1D`) — so on the
 * certificates list a routine open rendered exactly as loud as a theft report, and the
 * reframe from "Tag tampered" to "Opened" was undone by the colour.
 *
 * It also collapsed two independent axes. The status badge means OWNERSHIP state
 * (Active / Lost / Stolen); this one means SEAL state, and they render side by side.
 * Keeping colour for ownership and neutral for the seal makes them separable at a glance.
 *
 * Values are the same 50/200/800 zinc ramp `@repo/ui`'s `scanStatus.ts` already uses for
 * its neutral statuses, so this is a reused token set rather than a new colour. Not white:
 * a white chip on a white card disappears — the pale fill plus border keeps it present
 * while staying quiet. The `LockOpen` glyph carries the meaning.
 */
const NEUTRAL_RAMP = {
  backgroundColor: '#F4F4F5',
  borderColor: border.subtle,
  color: '#27272A',
} as const;

interface TagOpenedBadgeProps {
  /**
   * Passed straight to `StatusBadge`. The CARD must pass `dot`, because it renders
   * this badge directly beside the ownership status — and that one is already a dot,
   * so leaving this at the default `chip` put two different status grammars in one
   * row: a plain dot saying "Transfer" next to a filled pill saying "Opened".
   */
  variant?: 'chip' | 'dot';
  /**
   * Opens the "Opened" sheet (SealOpenedDrawer). Omitted on surfaces that don't own
   * that sheet's state (e.g. the certificates list card, which already navigates to
   * the certificate page on tap) — the badge then renders as a non-interactive status.
   */
  onClick?: () => void;
}

/**
 * VERA-628: the certificate's seal-opened status badge, shared by the certificate
 * page and the certificates list card (working-conventions §36 — the same
 * status/label/icon/colour combo was about to be hand-rolled a second time).
 *
 * Reframed from "Tag tampered" (an alarm) to a neutral factual status: a cut NTAG 424
 * tamper loop means the seal was opened, which is the expected, common case on a
 * Closure tag (the owner opening their own item). Rendered in the neutral ramp above —
 * see that comment for why this is deliberately NOT a status colour.
 */
export const TagOpenedBadge: React.FC<TagOpenedBadgeProps> = ({ onClick, variant }) => (
  <StatusBadge
    status='tampered'
    label='Opened'
    icon={<LockOpen size={14} strokeWidth={1.75} />}
    colors={NEUTRAL_RAMP}
    onClick={onClick}
    variant={variant}
  />
);

export default TagOpenedBadge;
